/**
 * Which CSS classes are "the buttons" in THIS project.
 *
 * The builder's global button controls (colour, radius, padding, hover effect)
 * were pinned to the CodeStitch vocabulary — `.cs-button-solid` and
 * `.cs-button-transparent`. Measured across the 200 crawled templates, 30 of
 * them name their buttons something else: 19 use `.button-solid` (the herocare
 * family), the rest `.btn`, `.button--primary`, `.cs-outline-button` and so on.
 * On those templates every button control was inert — the selector simply never
 * matched, so no amount of adjusting the panel moved the buttons.
 *
 * ## What the signal is, and what it is NOT
 *
 * The first version of this asked "does the template's CSS paint this class
 * like a button" (background + padding + display). Measured, that missed 9 of
 * the 170 CodeStitch templates, because the paint does not live in the template
 * at all — it lives in the builder's own `codestitch-baseline-css.js`, and the
 * template only OVERRIDES. learntoskatehb's entire `.cs-button-solid` rule is
 * four layout properties and no background.
 *
 * So the durable signal is the HTML: the class an `<a>` or `<button>` actually
 * WEARS. CSS is demoted to corroboration — something, anywhere in the project's
 * own CSS, must style the class, so a stray utility class in markup cannot
 * enrol itself. Measured on the same corpus: 167/170 on the CodeStitch
 * templates and 25/30 on the outliers.
 *
 * ## Three rules that keep this safe
 *
 * 1. **Strictly additive.** The CodeStitch pair is ALWAYS in the result. A
 *    project can only gain reach, never lose it, so no template that works
 *    today can regress — and a project whose buttons are purely CodeStitch
 *    derives exactly the historical pair, which keeps its emitted CSS
 *    byte-identical.
 * 2. **Deny by name, not by declaration.** A dark-mode toggle, a hamburger and
 *    a carousel arrow are all `<button>`s whose CSS is indistinguishable from a
 *    CTA's. They are excluded by NAME because nothing in their declarations
 *    separates them.
 * 3. **A single-use class still qualifies if the CSS declares it globally.** A
 *    footer swapped in from another template contributes one section and one
 *    button; requiring two sightings would leave exactly the cross-template
 *    case that motivated this unreachable. A root-level rule (`.btn-gold{}`,
 *    never `.pricing .btn-gold{}`) is the donor template declaring it a
 *    site-wide button, and that substitutes for frequency.
 *
 * Pure leaf module: csstree + nothing else, so both the browser render path and
 * the Node export path can import it.
 */

import * as csstree from '../vendor/csstree.esm.js';

/**
 * The historical constant. Always present in a derived vocabulary, which is
 * what makes the derivation additive-only.
 */
export const BASELINE_BUTTON_CLASSES = Object.freeze(['cs-button-solid', 'cs-button-transparent']);

/**
 * Classes the builder's own baseline stylesheet ships rules for. These need no
 * corroboration from the project's CSS — the builder itself is the evidence.
 * Mirrors the selectors in `shared/codestitch-baseline-css.js`.
 */
const BUILDER_STYLED_CLASSES = new Set([
  'cs-button-solid',
  'cs-button-transparent',
  'cs-button-outline',
  'cs-button-secondary',
  'button-transparent'
]);

/** A class name that reads as a button. */
const NAME_ALLOW = /(^|-)(buttons?|btns?|cta)(-|$)/i;

/**
 * Chrome that wears a button-ish name but is never the site's CTA. Excluded by
 * name because its declarations are identical to a real button's: a dark-mode
 * toggle and a hero CTA are both a padded box with a background.
 */
const NAME_DENY =
  /(dropdown|pagination|nav__|dark-?mode|hamburger|toggle|burger|close|scroll|back-to-top|^top-|-icon$|^icon-|wrapper$|group$|menu|drawer|slider|carousel|swiper|accordion|^tab$|tab-|play|social|share|cookie|consent)/i;

/** `.cs-btn-custom` opts an element OUT of global button styling by contract. */
export const BUTTON_OPT_OUT_CLASS = 'cs-btn-custom';

function parse(css) {
  try {
    return csstree.parse(String(css || ''), { parseValue: false, parseAtrulePrelude: false });
  } catch {
    // Unparsed CSS corroborates nothing. Callers treat an empty set as "no
    // evidence", never as "no buttons" — the baseline pair still ships.
    return null;
  }
}

/** Every class named anywhere in a stylesheet. */
function classesMentioned(css, into = new Set()) {
  const ast = parse(css);
  if (!ast) return into;
  csstree.walk(ast, {
    visit: 'ClassSelector',
    enter(node) {
      into.add(node.name);
    }
  });
  return into;
}

/**
 * Classes carrying a ROOT-LEVEL rule — a selector that is nothing but that one
 * class, plus any pseudos. `.btn-gold {}` and `.btn-gold:hover {}` qualify;
 * `.pricing .btn-gold {}` does not. A template styling a class unscoped is
 * declaring it a site-wide button.
 */
function rootLevelClasses(css, into = new Set()) {
  const ast = parse(css);
  if (!ast) return into;
  csstree.walk(ast, {
    visit: 'Rule',
    enter(rule) {
      if (rule.prelude?.type !== 'SelectorList') return;
      rule.prelude.children.forEach((selector) => {
        const parts = selector.children ? selector.children.toArray() : [];
        const classes = [];
        let structural = false;
        for (const node of parts) {
          if (node.type === 'ClassSelector') classes.push(node.name);
          else if (node.type !== 'PseudoClassSelector' && node.type !== 'PseudoElementSelector') {
            structural = true;
            break;
          }
        }
        if (!structural && classes.length === 1) into.add(classes[0]);
      });
    }
  });
  return into;
}

const CTA_TAG_RE = /<(a|button)\b([^>]*)>/gi;
const CLASS_ATTR_RE = /\bclass="([^"]*)"/i;

/**
 * Tally the classes worn by `<a>`/`<button>` across a section set.
 * Returns Map<className, {uses, sections}>.
 */
function tallyCtaClasses(sections) {
  const worn = new Map();
  for (const section of sections || []) {
    const html = typeof section?.html === 'string' ? section.html : '';
    if (!html) continue;
    const key = section.instanceId || section.sectionId || section.catalogKey || html.length;
    const seenHere = new Set();
    CTA_TAG_RE.lastIndex = 0;
    let match;
    while ((match = CTA_TAG_RE.exec(html))) {
      const classAttr = CLASS_ATTR_RE.exec(match[2]);
      if (!classAttr) continue;
      for (const name of classAttr[1].split(/\s+/)) {
        if (!name) continue;
        let record = worn.get(name);
        if (!record) {
          record = { uses: 0, sections: new Set() };
          worn.set(name, record);
        }
        record.uses += 1;
        if (!seenHere.has(name)) {
          record.sections.add(key);
          seenHere.add(name);
        }
      }
    }
  }
  return worn;
}

/**
 * Derive the button classes for a project.
 *
 * @param {Array} sections   every rendered section (nav + pages + footer)
 * @param {string} templateCSS  the assembled template support CSS
 * @returns {string[]} class names, baseline pair first, extras sorted
 */
export function deriveButtonClasses(sections, templateCSS) {
  const styled = classesMentioned(templateCSS);
  const rootLevel = rootLevelClasses(templateCSS);
  for (const section of sections || []) {
    if (typeof section?.css === 'string' && section.css) {
      classesMentioned(section.css, styled);
      rootLevelClasses(section.css, rootLevel);
    }
  }

  const extras = [];
  for (const [name, record] of tallyCtaClasses(sections)) {
    if (BASELINE_BUTTON_CLASSES.includes(name)) continue;
    if (!NAME_ALLOW.test(name) || NAME_DENY.test(name)) continue;
    if (!styled.has(name) && !BUILDER_STYLED_CLASSES.has(name)) continue;
    // Frequency OR a global declaration. The second half is what lets a single
    // swapped-in footer's button qualify on its first sighting.
    const common = record.sections.size >= 2 || record.uses >= 3;
    if (!common && !rootLevel.has(name) && !BUILDER_STYLED_CLASSES.has(name)) continue;
    extras.push(name);
  }
  extras.sort();
  return [...BASELINE_BUTTON_CLASSES, ...extras];
}

function escapeClassName(name) {
  return String(name).replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}

/**
 * Build the global button selector from a class list. Shape is preserved
 * exactly — `main :is(...):not(.cs-btn-custom)` — so a project deriving only
 * the baseline pair produces the historical string byte-for-byte.
 */
export function buildButtonSelector(classes) {
  const list = (classes && classes.length ? classes : BASELINE_BUTTON_CLASSES)
    .map((name) => `.${escapeClassName(name)}`)
    .join(', ');
  return `main :is(${list}):not(.${BUTTON_OPT_OUT_CLASS})`;
}

/** The historical selector, for the paths that have no project in scope. */
export const DEFAULT_BUTTON_SELECTOR = buildButtonSelector(BASELINE_BUTTON_CLASSES);

/**
 * Does the template supply its OWN button hover animation?
 *
 * The button panel showed "None" for every herocare button while all of them
 * visibly wiped on hover. Both statements were true and the UI was still wrong:
 * `buttonStyles.hoverEffect` records the BUILDER's override, and "None" meant
 * "no override", not "no animation". The template was doing it by hand —
 *
 *     .button-solid:before       { width: 0%;   transition: width .3s }
 *     .button-solid:hover:before { width: 100% }
 *
 * which is, exactly, the effect the builder itself calls `wipe`.
 *
 * Detecting it lets the panel say "Template default (wipe)" instead of "None".
 * This is DELIBERATELY display-only: nothing here changes emitted CSS, because
 * layering the builder's wipe on top of the template's own would paint two
 * pseudo-element fills over each other.
 *
 * @returns {{effect: string}|null} e.g. {effect:'wipe'} — null when the
 *   template animates nothing, or when we cannot tell.
 */
export function detectTemplateHoverEffect(sections, templateCSS, buttonClasses) {
  const classes = buttonClasses && buttonClasses.length ? buttonClasses : BASELINE_BUTTON_CLASSES;

  // `.bubbly-button` is a marker class in the markup, not a CSS shape.
  for (const section of sections || []) {
    if (typeof section?.html === 'string' && section.html.includes('bubbly-button')) {
      return { effect: 'bubble' };
    }
  }

  const css = [
    String(templateCSS || ''),
    ...(sections || []).map((s) => (typeof s?.css === 'string' ? s.css : ''))
  ].join('\n');
  if (!css) return null;

  const ast = parse(css);
  if (!ast) return null;

  const wanted = new Set(classes);
  // For each button class: does a `::before`/`::after` start at zero width and
  // reach full width under `:hover`? Both halves must be present — a static
  // zero-width pseudo is just an unused decoration.
  const zeroWidth = new Set();
  const fullWidthOnHover = new Set();

  csstree.walk(ast, {
    visit: 'Rule',
    enter(rule) {
      if (rule.prelude?.type !== 'SelectorList') return;
      rule.prelude.children.forEach((selector) => {
        const parts = selector.children ? selector.children.toArray() : [];
        const names = parts.filter((n) => n.type === 'ClassSelector').map((n) => n.name);
        const target = names.find((n) => wanted.has(n));
        if (!target) return;
        const hasPseudoEl = parts.some(
          (n) =>
            n.type === 'PseudoElementSelector' ||
            (n.type === 'PseudoClassSelector' && (n.name === 'before' || n.name === 'after'))
        );
        if (!hasPseudoEl) return;
        const isHover = parts.some((n) => n.type === 'PseudoClassSelector' && n.name === 'hover');

        rule.block?.children?.forEach((decl) => {
          if (decl.type !== 'Declaration') return;
          if (decl.property.toLowerCase() !== 'width') return;
          const value = csstree.generate(decl.value).trim();
          if (isHover) {
            if (/^100%$/.test(value)) fullWidthOnHover.add(target);
          } else if (/^0%?$/.test(value)) {
            zeroWidth.add(target);
          }
        });
      });
    }
  });

  for (const name of zeroWidth) {
    if (fullWidthOnHover.has(name)) return { effect: 'wipe' };
  }
  return null;
}
